import { createRoot } from 'react-dom/client';
import './index.css';
import { RouterProvider } from 'react-router-dom';
import router from '@/router.tsx';
// import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from '@/contexts/auth-context.tsx';
import { ThemeProvider } from '@/components/theme-provider.tsx';
import { Toaster } from '@/components/ui/sonner.tsx';
import SupportContactButton from '@/components/support-contact-button.tsx';

// Sentry.init({
//     dsn: "https://41e72b349d749c36a93a013dcc4c531f@o4508744948383744.ingest.de.sentry.io/4508744952905808",
//     integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
//     tracesSampleRate: 1.0,
//     tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
//     replaysSessionSampleRate: 0.1,
//     replaysOnErrorSampleRate: 1.0,
// });

import useVersionCheck from '@/hooks/useVersionCheck.tsx';
import reloadOnStaleChunk from '@/lib/reload-on-stale-chunk.ts';

// When a lazy-loaded chunk is missing (user is on old code after a deploy),
// reload the page so they land on the new version instead of a blank screen.
// Dynamic import() failures surface as unhandled promise rejections, not
// window 'error' events, so both listeners are needed.
window.addEventListener('error', (e) => reloadOnStaleChunk(e?.message));
window.addEventListener('unhandledrejection', (e) =>
  reloadOnStaleChunk(e?.reason?.message),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

function VersionCheck() {
  useVersionCheck();
  return null;
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
      <AuthProvider>
        <VersionCheck />
        <RouterProvider router={router} />
        <SupportContactButton />
        <Toaster
          richColors
          closeButton
          position="top-right"
          toastOptions={{
            style: {
              background: 'hsl(var(--background))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 'var(--radius)',
            },
            className: 'shadow-lg',
          }}
        />
      </AuthProvider>
    </ThemeProvider>
    <ReactQueryDevtools initialIsOpen={false} />
  </QueryClientProvider>,
);
